/**
 * PhoneBookingSmsButton — driver ↔ phone-booked-customer SMS.
 *
 * Outbound: "MESSAGE CUSTOMER" sends a canonical preset to the customer via
 * POST /api/driver/rides/:id/sms (number looked up server-side, never exposed).
 *
 * Inbound (SMS bridge): the customer's replies are stored on the ride thread
 * (ride_messages, inbound=true) and pushed over the `ride_message` WS event
 * (via="sms"). This component:
 *   - ALWAYS shows the conversation thread above the presets, with a clear
 *     empty state before the first reply.
 *   - renders stored replies (fetched from GET /api/rides/:id/messages).
 *   - shows a PERSISTENCE-BACKED unread badge: unread = stored inbound rows
 *     newer than a per-ride "last seen" marker (localStorage), so the badge
 *     survives refresh/reopen instead of living only in component memory.
 *
 * Privacy: only the sender's display name + message text are rendered — the
 * customer's phone number is never fetched or shown here.
 */
import React, { useState, useEffect } from "react";
import { MessageSquare, X, Loader2, Send, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { DRIVER_TEMPLATES } from "../../lib/rideMessageTemplates";
import { Button } from "../ui/button";

const EMPTY_THREAD_TEXT = "No customer replies yet — their texts will appear here.";

export const PhoneBookingSmsButton = ({ rideId, incomingMessage }) => {
  const [open, setOpen] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [thread, setThread] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);

  // Persisted "last seen" marker (ISO timestamp of the newest reply the driver
  // has already viewed). Survives refresh so the unread badge is reliable.
  const seenKey = `cd_sms_seen_${rideId}`;
  const [lastSeen, setLastSeen] = useState(() => {
    try {
      return localStorage.getItem(seenKey) || "";
    } catch (_) {
      return "";
    }
  });

  // Unread = stored inbound replies newer than the last-seen marker.
  const unread = thread.filter((m) => !lastSeen || (m.created_at || "") > lastSeen).length;

  const markAllRead = (msgs) => {
    const newest = (msgs || []).reduce(
      (acc, m) => ((m.created_at || "") > acc ? m.created_at : acc),
      lastSeen || ""
    );
    if (newest && newest !== lastSeen) {
      setLastSeen(newest);
      try {
        localStorage.setItem(seenKey, newest);
      } catch (_) {
        /* private mode — badge falls back to in-session only */
      }
    }
  };

  const fetchThread = async () => {
    setLoadingThread(true);
    try {
      const res = await api.get(`/rides/${rideId}/messages`);
      const msgs = (res?.data?.messages || []).filter(
        (m) => m.inbound === true || m.channel === "sms"
      );
      setThread(msgs);
      return msgs;
    } catch (_) {
      return [];
    } finally {
      setLoadingThread(false);
    }
  };

  // Initial load so the badge reflects stored replies even after a refresh.
  useEffect(() => {
    fetchThread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rideId]);

  // Live inbound reply for THIS ride: refresh the thread (recomputes unread).
  useEffect(() => {
    if (!incomingMessage) return;
    if (incomingMessage.via !== "sms" || incomingMessage.ride_id !== rideId) return;
    fetchThread().then((msgs) => {
      if (open) markAllRead(msgs);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingMessage]);

  const openDialog = async () => {
    setOpen(true);
    const msgs = await fetchThread();
    markAllRead(msgs);
  };

  const send = async (templateId) => {
    if (sendingId) return;
    setSendingId(templateId);
    try {
      const res = await api.post(`/driver/rides/${rideId}/sms`, { template_id: templateId });
      const data = res?.data || {};
      if (data.ok) {
        toast.success("Message sent to customer");
        setOpen(false);
      } else {
        toast.message(data.message || "Couldn't send the message.");
      }
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't send the message.");
    } finally {
      setSendingId(null);
    }
  };

  return (
    <>
      <div className="relative flex-1">
        <Button
          variant="default"
          size="sm"
          onClick={openDialog}
          className="w-full text-xs h-8 bg-[#0A0A0A] hover:bg-[#0A0A0A]/90"
          data-testid="sms-customer-btn"
        >
          <MessageSquare className="w-3 h-3 mr-1.5" /> MESSAGE CUSTOMER
        </Button>
        {unread > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E11D48] text-white text-[10px] font-black flex items-center justify-center"
            data-testid="sms-customer-unread-badge"
          >
            {unread}
          </span>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
          data-testid="phone-booking-sms-dialog"
          onMouseDown={() => !sendingId && setOpen(false)}
        >
          <div
            className="bg-white w-full max-w-md border border-border"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h3 className="font-heading text-base font-black tracking-tight flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Message customer
              </h3>
              <button
                onClick={() => !sendingId && setOpen(false)}
                className="text-muted-foreground hover:text-foreground p-1"
                data-testid="sms-customer-close"
                disabled={!!sendingId}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Conversation thread — ALWAYS visible, with an explicit empty state. */}
            <div
              className="px-4 pt-3 pb-3 max-h-48 overflow-y-auto border-b border-border"
              data-testid="sms-customer-thread"
            >
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
                <Inbox className="w-3 h-3" /> Customer replies
              </div>
              {loadingThread && thread.length === 0 ? (
                <div className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="sms-customer-thread-loading">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading replies…
                </div>
              ) : thread.length === 0 ? (
                <div className="text-sm text-muted-foreground italic" data-testid="sms-customer-thread-empty">
                  {EMPTY_THREAD_TEXT}
                </div>
              ) : (
                <div className="space-y-2">
                  {thread.map((m) => (
                    <div key={m.id} className="text-sm" data-testid="sms-customer-reply">
                      <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                        {m.from_name || "Customer"}
                      </span>
                      <div className="bg-secondary px-3 py-2 mt-0.5">{m.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 space-y-2">
              <p className="text-xs text-muted-foreground mb-1">
                Send the customer a quick text. They'll receive it as an SMS from Crystal Dispatch.
              </p>
              {DRIVER_TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => send(t.id)}
                  disabled={!!sendingId}
                  className="w-full text-left px-3 py-2.5 border border-border text-sm hover:bg-secondary transition-colors disabled:opacity-50 flex items-center justify-between gap-2"
                  data-testid={`sms-template-${t.id}`}
                >
                  <span>{t.body}</span>
                  {sendingId === t.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
                  ) : (
                    <Send className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PhoneBookingSmsButton;
